// OrgContext — fetches and provides org branding based on current subdomain.
//
// The backend reads X-Org-Slug header (set by nginx from subdomain) and
// returns branding config. This context fetches on mount and exposes:
//   - org: { id, slug, display_name, branding }
//   - loading: boolean
//
// Branding includes: logo_url, primary_color, secondary_color, company_name,
// tagline, custom_css. These are injected as CSS variables on :root.
// It also carries tenant settings: default_country, default_use_case,
// use_case_label, premium and siting_profile (docs/tenant-siting-profiles.md).

import React, { createContext, useContext, useEffect, useState } from 'react';

const ORG_URL = '/api/v1/orgs/current';

const defaultBranding = {
  logo_url: null,
  primary_color: '#1a73e8',
  secondary_color: '#34a853',
  favicon_url: null,
  company_name: 'Seidr',
  tagline: 'Nordic Site Intelligence',
  custom_css: null,
  siting_profile: null,
  countries: null,       // ISO-2 list the panel offers; null = all
};

const OrgContext = createContext({
  org: null,
  branding: defaultBranding,
  loading: true,
});

export function OrgProvider({ children }) {
  const [org, setOrg] = useState(null);
  const [branding, setBranding] = useState(defaultBranding);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrg = async () => {
      try {
        const apiBase = import.meta.env?.VITE_API_URL || '';
        const resp = await fetch(`${apiBase}${ORG_URL}`);
        if (resp.ok) {
          const data = await resp.json();
          setOrg(data);
          setBranding({ ...defaultBranding, ...data.branding });
          
          // Inject CSS variables
          const root = document.documentElement;
          const b = data.branding || {};
          if (b.primary_color) root.style.setProperty('--org-primary', b.primary_color);
          if (b.secondary_color) root.style.setProperty('--org-secondary', b.secondary_color);
          
          // Update page title with company name
          if (b.company_name) {
            document.title = `${b.company_name} | Site Intelligence`;
          }
          
          // Inject custom CSS if provided
          if (b.custom_css) {
            const style = document.createElement('style');
            style.id = 'org-custom-css';
            style.textContent = b.custom_css;
            document.head.appendChild(style);
          }
          
          // Update favicon if provided
          if (b.favicon_url) {
            let link = document.querySelector("link[rel~='icon']");
            if (!link) {
              link = document.createElement('link');
              link.rel = 'icon';
              document.head.appendChild(link);
            }
            link.href = b.favicon_url;
          }
        }
      } catch (err) {
        console.warn('Failed to fetch org branding:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchOrg();
    
    // Cleanup custom CSS on unmount
    return () => {
      const customStyle = document.getElementById('org-custom-css');
      if (customStyle) customStyle.remove();
    };
  }, []);

  return (
    <OrgContext.Provider value={{ org, branding, loading }}>
      {children}
    </OrgContext.Provider>
  );
}

export function useOrg() {
  return useContext(OrgContext);
}

export default OrgContext;
